/*
 * NextGenn engine · thirty seconds (the quiz).
 *
 * Three questions, one on screen at a time, then the two bottles to start
 * with. Everything a customer reads is already in the page, written by Liquid
 * from the section's settings: this file only decides what is showing. It
 * holds no copy and no answers. The mapping is the JSON table in the section,
 * and a table that is missing or broken degrades to the section's first two
 * bottles rather than to an empty result.
 *
 * The answers are buttons, not radios, on purpose. A radio group changes with
 * the arrow keys, and a screen that moves on when a radio changes would run
 * away from anyone reading the options with a keyboard. A button only acts
 * when it is pressed.
 *
 * Each Add is a real form inside the bag helper's <ngs-add>
 * (assets/ngs-cart.js): with the helper it calls NGSCart.add and the drawer
 * opens, without it the form posts to the cart the ordinary way.
 *
 * THE ONE EXCEPTION IS THE PAIR. The quiz answers "which of the twelve" with
 * two bottles and then used to ask for two decisions to act on one answer, so
 * there is a single button for both — one request, so the pair is either in
 * the bag or not, priced from the plates themselves rather than from anything
 * typed twice. It appears only when both plates are real products, and says
 * so once it has happened instead of sitting there inviting a second tap.
 */
if (!customElements.get('ngs-quiz')) {
  customElements.define(
    'ngs-quiz',
    class NgsQuiz extends HTMLElement {
      connectedCallback() {
        // The theme editor re-attaches sections; listeners must not double up.
        if (this.ready) return;
        this.ready = true;

        this.screens = Array.from(this.querySelectorAll('[data-question]'));
        this.result = this.querySelector('[data-result]');
        this.list = this.querySelector('[data-plates]');
        this.patch = this.querySelector('[data-patch]');
        // Kept in the owner's order: showing a result moves its two plates, and "the first two" must not drift.
        this.all = this.list ? Array.from(this.list.querySelectorAll('[data-product-id]')) : [];
        this.table = this.readTable();
        this.answers = {};
        this.index = 0;
        this.done = false;

        this.both = this.querySelector('[data-both]');
        this.bothButton = this.querySelector('[data-both-add]');
        this.bothStatus = this.querySelector('[data-both-status]');
        this.bothSaid = this.bothButton ? this.bothButton.textContent : '';

        this.addEventListener('click', this.onClick.bind(this));

        // In the theme editor, choosing a bottle in the sidebar shows that
        // bottle: otherwise every block is hidden behind three questions and
        // the owner is editing something they cannot see.
        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => this.preview(event.target));
          this.addEventListener('shopify:block:deselect', () => this.restore());
        }
      }

      readTable() {
        const node = this.querySelector('script[data-table]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      onClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const answer = target.closest('[data-answer]');
        if (answer && this.contains(answer)) {
          this.pick(answer);
          return;
        }
        if (target.closest('[data-back]')) {
          this.back();
          return;
        }
        if (target.closest('[data-both-add]')) {
          this.addBoth();
          return;
        }
        if (target.closest('[data-again]')) this.reset();
      }

      pick(button) {
        const screen = button.closest('[data-question]');
        if (!screen) return;

        screen.querySelectorAll('[data-answer]').forEach((other) => {
          other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
        });
        this.answers[screen.dataset.question] = button.dataset.answer;

        const next = this.screens.indexOf(screen) + 1;
        if (next < this.screens.length) {
          this.show(next);
        } else {
          this.finish();
        }
      }

      back() {
        // From the result, back is the last question; from a question, the one before.
        this.show(this.done ? this.screens.length - 1 : Math.max(0, this.index - 1));
      }

      reset() {
        this.answers = {};
        this.sayBoth('');
        this.querySelectorAll('[data-answer]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
        this.show(0);
      }

      show(index, quiet) {
        this.done = false;
        this.index = index;
        this.screens.forEach((screen, i) => {
          screen.hidden = i !== index;
        });
        if (this.result) this.result.hidden = true;
        if (!quiet) this.moveFocus(this.screens[index] && this.screens[index].querySelector('.ngs-quiz__q'));
      }

      finish(quiet) {
        const ids = this.lookup();
        const plates = this.all;
        let chosen = ids.map((id) => plates.find((plate) => plate.dataset.productId === id)).filter(Boolean);
        // No table, or a bottle the owner has removed: the first two, which are also the commonest answer.
        if (!chosen.length) chosen = plates.slice(0, 2);

        plates.forEach((plate) => {
          plate.hidden = true;
        });
        chosen.forEach((plate) => {
          this.setReason(plate);
          plate.hidden = false;
          // Treat first, then the moisturiser: the order they go on the face.
          this.list.appendChild(plate);
        });

        this.offerBoth(chosen);

        if (this.patch) this.patch.hidden = !(this.table && this.matches(this.table.patch_test));

        this.done = true;
        this.screens.forEach((screen) => {
          screen.hidden = true;
        });
        if (this.result) {
          this.result.hidden = false;
          if (!quiet) this.moveFocus(this.result.querySelector('[data-result-heading]'));
        }
      }

      lookup() {
        const table = this.table;
        if (!table || !table.pairs || !Array.isArray(table.questions)) return [];
        const [first, second, third] = table.questions;
        const row = table.pairs[this.answers[first] + '|' + this.answers[second]];
        const pair = row && row[this.answers[third]];
        return Array.isArray(pair) ? pair : [];
      }

      /* A rule is { question: answer }; it holds when every answer in it was given. */
      matches(rule) {
        if (!rule || typeof rule !== 'object') return false;
        const keys = Object.keys(rule);
        return keys.length > 0 && keys.every((key) => this.answers[key] === rule[key]);
      }

      /* The same bottle explains itself differently to two people: the serum to sensitive skin, the oil to redness. */
      setReason(plate) {
        const base = plate.querySelector('[data-why]');
        const alt = plate.querySelector('[data-why-alt]');
        const rules = (this.table && this.table.alt) || {};
        const useAlt = Boolean(alt) && this.matches(rules[plate.dataset.productId]);
        if (alt) alt.hidden = !useAlt;
        if (base) base.hidden = useAlt;
      }

      /* ── both bottles, one tap ────────────────────────────────────────── */

      /* Only a pair of real products, and only while the helper is there to
         put them in one request: two separate requests can half-succeed, and
         half a routine is worse than a button that was never offered. */
      offerBoth(chosen) {
        if (!this.both || !this.bothButton) return;
        this.pair = chosen.filter((plate) => plate.dataset.variant);
        var enough = this.pair.length === 2 && Boolean(window.NGSCart);
        this.both.hidden = !enough;
        this.sayBoth('');
        if (!enough) return;

        var pennies = this.pair.reduce(function (total, plate) {
          return total + (Number(plate.dataset.price) || 0);
        }, 0);
        this.bothButton.disabled = false;
        this.bothButton.textContent = this.bothSaid.replace('[sum]', '$' + Math.round(pennies / 100));
      }

      addBoth() {
        if (!this.pair || this.pair.length !== 2 || !window.NGSCart) return;
        this.bothButton.disabled = true;
        this.both.classList.add('is-adding');
        window.NGSCart.add(
          this.pair.map(function (plate) {
            return { id: plate.dataset.variant, quantity: 1 };
          })
        )
          .then(() => {
            this.both.classList.remove('is-adding');
            /* The drawer is open with both lines in it; a live button here
               would only be a second, duplicate tap. */
            this.bothButton.hidden = true;
            this.sayBoth(this.both.dataset.done || '');
          })
          .catch((error) => {
            this.both.classList.remove('is-adding');
            this.bothButton.disabled = false;
            this.sayBoth(error && error.response ? error.message : this.both.dataset.error || '');
          });
      }

      sayBoth(words) {
        if (!this.bothStatus) return;
        this.bothStatus.textContent = words;
        this.bothStatus.hidden = !words;
        if (!words && this.bothButton) this.bothButton.hidden = false;
      }

      moveFocus(node) {
        // preventScroll: the stage is already in view, and a jump to the heading reads as a glitch.
        if (node) node.focus({ preventScroll: true });
      }

      /* ── the theme editor ─────────────────────────────────────────────── */

      preview(plate) {
        if (!plate || !plate.matches('[data-product-id]')) return;
        this.screens.forEach((screen) => {
          screen.hidden = true;
        });
        this.all.forEach((other) => {
          other.hidden = other !== plate;
        });
        if (this.result) this.result.hidden = false;
      }

      restore() {
        // Quietly: taking focus here would pull it out of the editor's sidebar, mid-typing.
        if (this.done) {
          this.finish(true);
        } else {
          this.show(this.index, true);
        }
      }
    }
  );
}
